import { Button, Select, SelectItem } from '@heroui/react'
import {
  BULK_INVITE_CANONICAL_FIELDS,
  type BulkInviteCanonicalField,
  type BulkInviteColumn,
  type BulkInviteResolvedColumn,
  type ParsedBulkInviteError,
} from '@qse/api'
import { Alert } from '@qse/ui'
import { AlertTriangle, Wand2 } from 'lucide-react'

import {
  CANONICAL_FIELD_LABELS,
  getDuplicateFields,
  getUnresolvedRequired,
  isMappingValid,
  isRequiredField,
} from './helpers'

interface ColumnMappingStepProps {
  fileName?: string
  columns: BulkInviteColumn[]
  /** Current mapping — auto-resolved on arrival, then the admin's edits. */
  mapping: BulkInviteResolvedColumn[]
  isConfirming: boolean
  /**
   * A failure from confirming (quota, a rejected override). It surfaces here rather
   * than on upload because this screen is where it can actually be acted on.
   */
  error: ParsedBulkInviteError | null
  onChange: (mapping: BulkInviteResolvedColumn[]) => void
  onBack: () => void
  onConfirm: () => void
}

/** Sentinel for "no column selected" — Select keys must be strings. */
const UNSET_KEY = '__unset__'

/** The column (if any) a field is currently pointing at. */
const columnForField = (
  mapping: BulkInviteResolvedColumn[],
  field: BulkInviteCanonicalField,
): BulkInviteResolvedColumn | undefined => mapping.find(column => column.field === field)

/**
 * Columns selectable for `field`'s dropdown: anything not claimed by a
 * *different* field, plus whatever `field` itself currently holds (so its own
 * selection never disappears from its own list, and a pre-existing duplicate
 * from the server's auto-mapping stays visible in both colliding fields'
 * lists until the admin picks one).
 */
const availableColumnsForField = (
  columns: BulkInviteColumn[],
  mapping: BulkInviteResolvedColumn[],
  field: BulkInviteCanonicalField,
): BulkInviteColumn[] =>
  columns.filter(column => {
    const resolved = mapping.find(m => m.index === column.index)

    return !resolved?.field || resolved.field === field
  })

const columnOptionLabel = (column: BulkInviteColumn) => column.header || `Column ${column.index + 1}`

/** Amber notice — these flags are things to fix, not failures; the shared Alert is red-only. */
const MappingFlag = ({ title, detail }: { title: string; detail: string }) => (
  <div className="flex items-start gap-3 rounded-lg border border-amber-500/30 bg-amber-500/10 p-3">
    <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
    <div>
      <p className="text-sm font-medium text-amber-900 dark:text-amber-100">{title}</p>
      <p className="text-sm text-amber-900/80 dark:text-amber-100/80">{detail}</p>
    </div>
  </div>
)

/**
 * Review Column Mapping — the step between Upload and Review.
 *
 * Auto-mapping is a proposal, not a verdict: every column is shown with how we read
 * it and can be re-pointed or ignored. Two things make this screen work and are worth
 * preserving:
 *  - **Sample values.** A header of "Contact" is ambiguous; a column of "+962…" next
 *    to one of "a@b.com" is not. The samples resolve at a glance what headers can't.
 *  - **Edits are local.** Nothing here calls the server. The expensive dry run (a
 *    Cognito lookup per email) runs once, on Confirm — re-running it per dropdown
 *    change would hammer Cognito on a 1,000-row file.
 */
export default function ColumnMappingStep({
  fileName,
  columns,
  mapping,
  isConfirming,
  error,
  onChange,
  onBack,
  onConfirm,
}: ColumnMappingStepProps) {
  const unresolvedRequired = getUnresolvedRequired(mapping)
  const duplicateFields = getDuplicateFields(mapping)
  const canConfirm = isMappingValid(mapping) && !isConfirming

  const handleColumnChange = (field: BulkInviteCanonicalField, columnIndex: number | null) => {
    onChange(
      mapping.map(column => {
        if (column.index === columnIndex) {
          // Explicit pick — no longer an auto-match.
          return { ...column, field, matchedBy: 'exact' as const }
        }

        // Clear every other column that previously held this field — covers both
        // reassigning the field to a different column and resolving a pre-existing
        // duplicate by choosing which one wins. When columnIndex is null (the
        // "No column selected" pick), this clears the field with nothing replacing it.
        if (column.field === field) {
          return { ...column, field: null }
        }

        return column
      }),
    )
  }

  return (
    <div className="flex flex-col gap-5 py-5 w-full">
      <div>
        <p className="text-sm text-gray-600 dark:text-[#AFAFAF]">
          Check how we read each column of your file. Change anything we got wrong — nothing is sent
          yet.
        </p>
        {fileName && (
          <p className="text-xs text-gray-500 dark:text-[#AFAFAF] break-all mt-1">{fileName}</p>
        )}
      </div>

      {unresolvedRequired.length > 0 && (
        <MappingFlag
          title="A required field isn't mapped yet"
          detail={`Point a column at: ${unresolvedRequired
            .map(field => CANONICAL_FIELD_LABELS[field])
            .join(', ')}. If your file doesn't have it, add the column and upload again.`}
        />
      )}

      {duplicateFields.length > 0 && (
        <MappingFlag
          title="Two columns map to the same field"
          detail={`Open the row for ${duplicateFields
            .map(field => CANONICAL_FIELD_LABELS[field])
            .join(', ')} and choose which column supplies it.`}
        />
      )}

      {error && (
        <Alert
          color="danger"
          description="Adjust the columns below and try again."
          title={error.message}
        />
      )}

      <div className="flex flex-col gap-3 max-h-80 overflow-auto">
        {BULK_INVITE_CANONICAL_FIELDS.map(field => {
          const resolved = columnForField(mapping, field)
          const isFuzzy = resolved?.matchedBy === 'fuzzy'
          const isUnresolved = !resolved
          const columnOptions = [
            { key: UNSET_KEY, label: 'No column selected', description: undefined as string | undefined },
            ...availableColumnsForField(columns, mapping, field).map(column => ({
              key: String(column.index),
              label: columnOptionLabel(column),
              description: column.samples.length > 0 ? column.samples.join(' · ') : 'No sample values',
            })),
          ]

          return (
            <div
              key={field}
              className="flex flex-col sm:flex-row sm:items-center gap-3 rounded-lg border p-3 border-gray-200 dark:border-[#2A2A2A]"
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium break-all">
                    {CANONICAL_FIELD_LABELS[field]}
                    {isRequiredField(field) && (
                      <span className="text-gray-400 dark:text-[#AFAFAF] font-normal"> (required)</span>
                    )}
                  </span>
                  {isFuzzy && (
                    <span className="flex items-center gap-1 text-[11px] font-medium px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-600 dark:text-amber-400">
                      <Wand2 className="w-3 h-3" />
                      Best guess
                    </span>
                  )}
                  {isUnresolved && isRequiredField(field) && (
                    <span className="flex items-center gap-1 text-[11px] font-medium px-1.5 py-0.5 rounded bg-gray-400/15 text-gray-500 dark:text-gray-300">
                      <AlertTriangle className="w-3 h-3" />
                      Not mapped
                    </span>
                  )}
                </div>
              </div>

              <Select
                aria-label={`Column for ${CANONICAL_FIELD_LABELS[field]}`}
                className="sm:w-64"
                isDisabled={isConfirming}
                radius="sm"
                selectedKeys={new Set([resolved ? String(resolved.index) : UNSET_KEY])}
                selectionMode="single"
                size="sm"
                classNames={{
                  popoverContent:
                    'bg-white dark:bg-[#343537] border border-gray-200 dark:border-[#707070]',
                }}
                onSelectionChange={keys => {
                  const key = Array.from(keys)[0] as string | undefined

                  if (!key) return
                  handleColumnChange(field, key === UNSET_KEY ? null : Number(key))
                }}
              >
                {columnOptions.map(option => (
                  <SelectItem
                    key={option.key}
                    className="text-gray-900 dark:text-white"
                    description={option.description}
                  >
                    {option.label}
                  </SelectItem>
                ))}
              </Select>
            </div>
          )
        })}
      </div>

      <div className="flex justify-end gap-3 w-full">
        <Button isDisabled={isConfirming} radius="sm" variant="flat" onPress={onBack}>
          Back
        </Button>
        <Button
          color="primary"
          isDisabled={!canConfirm}
          isLoading={isConfirming}
          radius="sm"
          onPress={onConfirm}
        >
          Continue
        </Button>
      </div>
    </div>
  )
}
